import type { User, UserRole } from './api';
import { tokenRefreshService } from './tokenRefresh';

/**
 * Get the current user from localStorage
 */
export const getCurrentUser = (): User | null => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr) as User;
  } catch {
    return null;
  }
};

/**
 * Logout the current user
 */
export const logout = async (): Promise<void> => {
  await tokenRefreshService.logout();
  // Force page reload to clear any cached state
  window.location.href = '/';
};

/**
 * Check if the current user has the required role
 */
export const hasRole = (requiredRole: UserRole): boolean => {
  const user = getCurrentUser();
  if (!user) return false;
  
  // Super admin has access to everything
  if (user.roles?.includes('super_admin')) return true;
  
  // Check if user has the required role
  return user.roles?.includes(requiredRole) || false;
};

/**
 * Check if the current user is a super admin
 */
export const isSuperAdmin = (): boolean => {
  return hasRole('super_admin');
};

/**
 * Check if the current user is an expert
 */
export const isExpert = (): boolean => {
  const user = getCurrentUser();
  if (!user) return false;
  return user.roles?.some(role => ['expert', 'admin', 'super_admin'].includes(role)) || false;
};

/**
 * Check if the current user has admin privileges (admin, expert, or super_admin role)
 */
export const isAdmin = (): boolean => {
  const user = getCurrentUser();
  if (!user) return false;

  return user.roles?.some(role => ['super_admin', 'expert', 'admin'].includes(role)) || false;
};

/**
 * Check if the current user is exclusively an admin — has the 'admin' role
 * and is NOT a super_admin. Used to restrict Admins to the Users feature only.
 */
export const isAdminOnly = (): boolean => {
  const user = getCurrentUser();
  if (!user?.roles) return false;
  return user.roles.includes('admin') && !user.roles.includes('super_admin');
};

/**
 * Check if the current user is exclusively an expert — has the 'expert' role
 * and is NOT an admin or super_admin. Used to restrict Experts to the
 * Expert Dashboard feature only.
 */
export const isExpertOnly = (): boolean => {
  const user = getCurrentUser();
  if (!user?.roles) return false;
  return user.roles.includes('expert') && !user.roles.includes('admin') && !user.roles.includes('super_admin');
};

/**
 * Get user display name
 */
export const getUserDisplayName = (): string => {
  const user = getCurrentUser();
  return user?.displayName || 'Unknown';
};

/**
 * Get user role display name
 */
export const getUserRoleDisplayName = (): string => {
  const user = getCurrentUser();
  if (!user || !user.roles || user.roles.length === 0) return 'Unknown';
  
  // Show primary role (first in array) or all roles if multiple
  const primaryRole = user.roles[0];
  
  const roleNames: Record<UserRole, string> = {
    'super_admin': 'Super Admin',
    'expert': 'Expert',
    'admin': 'Admin',
    'user': 'User'
  };
  
  if (user.roles.length === 1) {
    return roleNames[primaryRole] || 'Unknown';
  }
  
  // Multiple roles - show all
  return user.roles.map(role => roleNames[role] || role).join(', ');
};
